import Vue from 'vue';
import Multiselect from 'vue-multiselect';
import Auth from 'src/api/auth';
import Project from 'src/api/project';
import { CLEAR_REQUEST_ERRORS } from 'src/store/types';
import placeholderPersonPNG from 'src/assets/placeholder_person.png';
import { BfModal, BfDialog, BfButton } from "components/Butterfly";
export default Vue.extend({
    components: {
        Multiselect,
        BfModal,
        BfDialog,
        BfButton,
    },
    props: {
        projectId: { type: Number, required: true },
        activeUser: { type: Object, required: true },
        projectCreatedById: { type: Number, required: true },
        projectMembers: { type: Array, required: false, default: () => [] },
        isLoading: { type: Boolean, required: false, default: false },
    },
    data() {
        return {
            fetchingUsers: false,
            addingOrRemoving: false,
            placeholderPersonPNG,
            selectedUser: null,
            addedOnceError: false,
            errorDeleting: false,
            users: [],
            updating: false,
            removeModalVisible: false,
            userToRemove: null,
        };
    },
    computed: {
        projectMembersUserIds() {
            return this.projectMembers.map(user => user.id);
        },
        /**
         * Can this user be removed from the project by the current active user.
         * Only site admin and/or project creator can remove.
         * This controls whether the Remove button is shown for each member.
         */
        allowedToRemove() {
            return this.activeUser.admin || this.activeUser.id === this.projectCreatedById;
        },
        /**
         * indicator of whether component is in usable lifetime
         */
        isAlive() {
            return !this._isDestroyed && !this._isBeingDestroyed;
        },
    },
    watch: {
        /*
          When isLoading changes from true to false this means that an updated list of project
          members is available so ensure that the toggle for the mask is turned off.
          This is the only time that `addingOrRemoving` is set to false after a user is added
          or removed.
         */
        isLoading(new_isLoading, old_isLoading) {
            if (old_isLoading === true && new_isLoading === false)
                this.addingOrRemoving = false;
        }
    },
    methods: {
        hideRemoveModal(cancel = false) {
            if (cancel) {
                this.userToRemove = null;
            }
            this.removeModalVisible = false;
        },
        promptToRemoveUser(user) {
            this.userToRemove = user;
            this.removeModalVisible = true;
        },
        /**
         * removing specified user from project
         */
        async removeUser() {
            this.hideRemoveModal();
            this.errorDeleting = false;
            if (!this.userToRemove)
                return;
            try {
                this.addingOrRemoving = true;
                await Project.removeUserFromProject(this.projectId, this.userToRemove.id);
                if (!this.isAlive)
                    return;
                this.$emit('users-updated');
            }
            catch (e) {
                if (!this.isAlive)
                    return;
                this.errorDeleting = true;
                this.addingOrRemoving = false;
            }
        },
        /**
         * adding selected user to project
         */
        async addUser() {
            if (!this.selectedUser) {
                return;
            }
            this.addedOnceError = false;
            this.addingOrRemoving = true;
            try {
                const result = await Project.addUserToProject(this.projectId, this.selectedUser);
                if (!this.isAlive)
                    return;
                this.selectedUser = null;
                this.$emit('users-updated');
            }
            catch (error) {
                if (!this.isAlive)
                    return;
                let nonFieldErrors = error.json()['non_field_errors'] || [''];
                if (nonFieldErrors[0].toLowerCase().includes('added once')) {
                    this.addedOnceError = nonFieldErrors[0];
                    this.$store.dispatch(CLEAR_REQUEST_ERRORS); // custom error handling
                }
                this.addingOrRemoving = false;
            }
        },
        /**
         * Fetching list of users for multi-select
         * @param query
         */
        async fetchUsers(query) {
            if (query?.length === 0) {
                this.users = [];
                return;
            }
            try {
                this.fetchingUsers = true;
                const { results } = await Auth.getUserList(query);
                if (!this.isAlive)
                    return;
                this.users = results.filter(user => !this.projectMembersUserIds.includes(user.id));
            }
            catch {
                if (!this.isAlive)
                    return;
                this.users = [];
            }
            finally {
                if (!this.isAlive)
                    return;
                this.fetchingUsers = false;
            }
        },
        /**
         * text to show in multi-select when limit hit
         * @param count
         */
        limitText(count) {
            return `and ${count} other users`;
        },
        /**
         * formatting for user
         * @param user
         */
        userLabel(user) {
            return `${user.first_name} ${user.last_name} (${user.email})`;
        },
        // This will prevent the button from clearing the search box
        // and giving the user the false impression that an invalid
        // input has been accepted
        addMouseDown(event) {
            if (!this.selectedUser) {
                event.preventDefault();
            }
        },
    }
});
